import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider, Typography } from "@mui/material";

import { getServiceReviewsPageRoute } from "../../../../routers/routeUtils";

import { Service } from "../../types/service.type";
import GoHireSlider from "../../components/GoHireSlider";
import EmptyReviewsFigure from "../../assets/images/empty-reviews.png";
import { useServiceFeedbackPaginated } from "../../api/queries/useServiceFeedback";
import { ServiceFeedbackCard, ServiceFeedbackCardLoader } from "../../components/ServiceFeedbackCard";
import { SeeAllReviewsButtonIcon, ServiceReviewsIcon } from "../../assets/icons/service-details-icons";

const ServiceReviews = ({ service, isServiceLoading }: { service?: Service; isServiceLoading: boolean }) => {
  const { t } = useTranslation("goService", { keyPrefix: "servicePage" });
  const navigate = useNavigate();
  const { data: reviews, isLoading: isReviewsLoading } = useServiceFeedbackPaginated({
    // userId: service?.user?.id,
    serviceId: service?.id,
  });

  if (!isReviewsLoading && !isServiceLoading && reviews?.length === 0) {
    return <EmptyServiceReviews />;
  }

  const handleSeeAllReviews = () => {
    navigate(getServiceReviewsPageRoute(service?.id!));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        padding: "24px",
        borderRadius: "12px",
        background: "#FFF",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={styles.title}>
          <ServiceReviewsIcon />
          {t("serviceReviews")}
        </Typography>
        <Button
          onClick={handleSeeAllReviews}
          variant="text"
          sx={styles.seeAllReviews}
          endIcon={<SeeAllReviewsButtonIcon />}
        >
          {t("seeAllReviews")}
        </Button>
      </Box>
      <Divider sx={styles.divider} />
      <GoHireSlider
        items={reviews?.map((feedback) => <ServiceFeedbackCard key={feedback.id} feedback={feedback} />) || []}
        isLoading={isReviewsLoading || isServiceLoading}
        loadingComponent={<ServiceFeedbackCardLoader />}
        loadingCount={1}
        numberOfSlidesPerView={1}
      />
    </Box>
  );
};

const EmptyServiceReviews = () => {
  const { t } = useTranslation("goService", { keyPrefix: "servicePage" });
  return (
    <Box sx={styles.emptyContainer}>
      <Box sx={styles.emptyImage} component="img" src={EmptyReviewsFigure} alt="Empty Reviews" />
      <Typography sx={styles.emptyText}>{t("noReviews")}</Typography>
    </Box>
  );
};

export default ServiceReviews;

const styles = {
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  seeAllReviews: {
    padding: 0,
    "&:hover": {
      background: "none",
    },
  },
  divider: {
    mx: "-24px",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    padding: "24px",
    borderRadius: "12px",
    background: "#FFF",
  },
  emptyImage: {
    width: "107px",
    height: "80px",
  },
  emptyText: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
};
